import React, { useEffect, useMemo, useRef, useState } from 'react';
import StatBox from '../ui/StatBox';
import { GameIntro, GameOver, GameState, LevelPicker, isTypingKey, useLevelProgress, useSaveGameResult } from './GameShell';
import { playCorrectSound, playWrongSound, playMissSound, playPopSound, playGameOverSound, playSuccessSound } from '../../utils/audio';
import { User } from '../../types';
import { ScorePops, useScorePops } from '../ui/ScorePops';

type Mat = 'dirt' | 'wood' | 'stone' | 'iron' | 'gold' | 'diamond' | 'sand' | 'cactus' | 'sandstone' | 'snow' | 'ice'
  | 'netherrack' | 'magma' | 'obsidian' | 'cloud' | 'crystal' | 'star' | 'leaves' | 'bamboo' | 'moss' | 'emerald'
  | 'coral' | 'seaglass' | 'pearl' | 'sugar' | 'gumdrop' | 'chocolate' | 'peppermint' | 'darkwood' | 'pumpkin' | 'bone'
  | 'soulgem' | 'moonrock' | 'metal' | 'goo' | 'meteor';

const STONE = ['#8a8a8a', '#7a7a7a', '#9b9b9b', '#6b6b6b'];
const MATS: Record<Mat, { label: string; base: string[]; speck?: string; stripes?: boolean; pts: number }> = {
  dirt: { label: 'Dirt', base: ['#8b5a2b', '#7a4a22', '#9b6a3a', '#6e4320'], pts: 5 },
  wood: { label: 'Wood', base: ['#a0703c', '#8a5a2b', '#b5834a', '#6b4423'], stripes: true, pts: 10 },
  stone: { label: 'Stone', base: STONE, pts: 15 },
  iron: { label: 'Iron', base: STONE, speck: '#e0b48f', pts: 25 },
  gold: { label: 'Gold', base: STONE, speck: '#facc15', pts: 35 },
  diamond: { label: 'Diamond', base: STONE, speck: '#5eead4', pts: 50 },
  sand: { label: 'Sand', base: ['#e8d39a', '#dcc387', '#f0dca8', '#cfb676'], pts: 5 },
  cactus: { label: 'Cactus', base: ['#2f9e44', '#237a34', '#3fb456', '#1d6b2c'], stripes: true, pts: 10 },
  sandstone: { label: 'Sandstone', base: ['#d9c08a', '#cbb078', '#e4cc96', '#bfa46c'], pts: 15 },
  snow: { label: 'Snow', base: ['#f8fafc', '#eef2f7', '#ffffff', '#e2e8f0'], pts: 5 },
  ice: { label: 'Ice', base: ['#a5d8ff', '#8ec5f2', '#bde3ff', '#74b4e8'], pts: 15 },
  netherrack: { label: 'Hot Rock', base: ['#7f1d1d', '#991b1b', '#6b1515', '#b91c1c'], pts: 10 },
  magma: { label: 'Magma', base: ['#7c2d12', '#9a3412', '#451a03', '#7c2d12'], speck: '#fb923c', pts: 25 },
  obsidian: { label: 'Obsidian', base: ['#1e1b2e', '#2e1065', '#171326', '#3b2a5c'], pts: 35 },
  cloud: { label: 'Cloud', base: ['#ffffff', '#f1f5f9', '#e2e8f0', '#ffffff'], pts: 10 },
  crystal: { label: 'Crystal', base: ['#c4b5fd', '#a78bfa', '#ddd6fe', '#8b5cf6'], speck: '#f0abfc', pts: 30 },
  star: { label: 'Star', base: ['#1e3a8a', '#1e40af', '#172554', '#1d4ed8'], speck: '#fde047', pts: 45 },
  leaves: { label: 'Leaves', base: ['#15803d', '#166534', '#22c55e', '#14532d'], pts: 10 },
  bamboo: { label: 'Bamboo', base: ['#a3e635', '#84cc16', '#bef264', '#65a30d'], stripes: true, pts: 15 },
  moss: { label: 'Mossy Stone', base: STONE, speck: '#65a30d', pts: 25 },
  emerald: { label: 'Emerald', base: STONE, speck: '#34d399', pts: 50 },
  coral: { label: 'Coral', base: ['#fb7185', '#f43f5e', '#fda4af', '#e11d48'], pts: 20 },
  seaglass: { label: 'Sea Glass', base: ['#5eead4', '#2dd4bf', '#99f6e4', '#14b8a6'], pts: 30 },
  pearl: { label: 'Pearl', base: ['#f5f5f4', '#e7e5e4', '#fafaf9', '#d6d3d1'], speck: '#f9a8d4', pts: 50 },
  sugar: { label: 'Sugar', base: ['#fdf2f8', '#fce7f3', '#ffffff', '#fbcfe8'], pts: 10 },
  gumdrop: { label: 'Gumdrop', base: ['#f472b6', '#a78bfa', '#34d399', '#fbbf24'], pts: 20 },
  chocolate: { label: 'Chocolate', base: ['#78350f', '#92400e', '#451a03', '#713f12'], stripes: true, pts: 30 },
  peppermint: { label: 'Peppermint', base: ['#ef4444', '#dc2626', '#ffffff', '#fef2f2'], stripes: true, pts: 40 },
  darkwood: { label: 'Dark Wood', base: ['#3f2a14', '#2b1d0e', '#4a3218', '#1f150a'], stripes: true, pts: 15 },
  pumpkin: { label: 'Pumpkin', base: ['#f97316', '#ea580c', '#fb923c', '#c2410c'], stripes: true, pts: 25 },
  bone: { label: 'Bone', base: ['#f5f5f4', '#e7e5e4', '#d6d3d1', '#fafaf9'], pts: 35 },
  soulgem: { label: 'Spirit Gem', base: ['#1e1b4b', '#312e81', '#172554', '#3730a3'], speck: '#a5f3fc', pts: 50 },
  moonrock: { label: 'Moon Rock', base: ['#d4d4d8', '#a1a1aa', '#e4e4e7', '#71717a'], pts: 20 },
  metal: { label: 'Metal', base: ['#94a3b8', '#64748b', '#cbd5e1', '#475569'], stripes: true, pts: 30 },
  goo: { label: 'Alien Goo', base: ['#4ade80', '#22c55e', '#86efac', '#16a34a'], speck: '#bbf7d0', pts: 40 },
  meteor: { label: 'Meteorite', base: ['#292524', '#1c1917', '#44403c', '#0c0a09'], speck: '#f97316', pts: 60 },
};

interface Character { name: string; skin: string; hair?: string; shirt: string; pants: string; eye: string }
interface World {
  name: string; icon: string; sky: [string, string]; night: [string, string]; grass: string; dirt: [string, string]; deep: [string, string];
  decor: 'tree' | 'cactus' | 'pine' | 'palm' | 'kelp' | 'lollipop' | 'deadtree' | 'flag' | 'none'; enemy: Character; speed: number;
  materials: Mat[]; recipes: { name: string; icon: string; needs: Partial<Record<Mat, number>> }[]; words: string[];
  sun?: 'none' | 'earth'; // default: a sun by day and a moon by night
  ceiling?: string; // cave worlds: color of the rocky strip along the top
  clouds?: number; // cloud opacity, default 1
  floaters?: string; // emoji that drift across the sky instead of clouds
  stars?: boolean;
}

// Each world has its own scenery, night monster, materials, crafting goals and vocabulary.
const WORLDS: World[] = [
  {
    name: 'Meadow', icon: '🌳', sky: ['#38bdf8', '#bae6fd'], night: ['#1e1b4b', '#3730a3'], grass: '#4caf50', dirt: ['#7a4a22', '#8b5a2b'], deep: ['#6b6b6b', '#7a7a7a'],
    decor: 'tree', speed: 4.5, enemy: { name: 'Zombie', skin: '#5fae4a', shirt: '#0e7490', pants: '#4338ca', eye: '#111' },
    materials: ['dirt', 'wood', 'stone'],
    recipes: [
      { name: 'Crafting Table', icon: '🧰', needs: { wood: 3 } },
      { name: 'Stone Pickaxe', icon: '⛏️', needs: { stone: 2, wood: 1 } },
      { name: 'Cozy Cabin', icon: '🏡', needs: { dirt: 2, wood: 2, stone: 2 } },
    ],
    words: ['sun', 'cat', 'dog', 'hat', 'bee', 'cow', 'pig', 'egg', 'map', 'cup', 'box', 'fan', 'jam', 'log', 'mud', 'oak', 'hen', 'bug', 'fox', 'owl', 'leaf', 'tree', 'farm', 'frog', 'duck', 'rain', 'seed', 'hill', 'nest', 'barn', 'corn', 'goat', 'pond', 'bird', 'lamb', 'wood', 'rock', 'path', 'grass', 'apple', 'sheep', 'berry', 'bunny'],
  },
  {
    name: 'Desert', icon: '🏜️', sky: ['#fb923c', '#fde68a'], night: ['#2a1a3e', '#5b3a6e'], grass: '#e8c872', dirt: ['#dcc387', '#e8d39a'], deep: ['#bfa46c', '#cbb078'],
    decor: 'cactus', speed: 5.5, enemy: { name: 'Mummy', skin: '#e7e5e4', hair: '#d6d3d1', shirt: '#d6d3d1', pants: '#a8a29e', eye: '#f59e0b' },
    materials: ['sand', 'cactus', 'sandstone', 'gold'],
    recipes: [
      { name: 'Cactus Juice', icon: '🥤', needs: { cactus: 3 } },
      { name: 'Sand Castle', icon: '🏰', needs: { sand: 3, sandstone: 2 } },
      { name: 'Golden Scarab', icon: '🪲', needs: { gold: 3 } },
    ],
    words: ['sand', 'dune', 'heat', 'camel', 'oasis', 'snake', 'tomb', 'gold', 'sunny', 'palm', 'tent', 'hike', 'dusty', 'mirage', 'lizard', 'pyramid', 'mummy', 'jewel', 'water', 'storm', 'rocky', 'canyon', 'cactus', 'desert', 'sunset', 'coyote', 'vulture', 'temple', 'compass', 'nomad', 'sphinx', 'scarab', 'golden', 'thirsty', 'journey'],
  },
  {
    name: 'Snowy Peaks', icon: '🏔️', sky: ['#93c5fd', '#f1f5f9'], night: ['#0f172a', '#1e3a8a'], grass: '#f8fafc', dirt: ['#cbd5e1', '#e2e8f0'], deep: ['#64748b', '#7c8aa0'],
    decor: 'pine', speed: 6.5, enemy: { name: 'Snow Monster', skin: '#f8fafc', hair: '#e2e8f0', shirt: '#e2e8f0', pants: '#94a3b8', eye: '#0284c7' },
    materials: ['snow', 'ice', 'iron', 'diamond'],
    recipes: [
      { name: 'Snowman', icon: '⛄', needs: { snow: 3 } },
      { name: 'Igloo', icon: '🛖', needs: { ice: 3, snow: 2 } },
      { name: 'Ice Skates', icon: '⛸️', needs: { iron: 3 } },
      { name: 'Diamond Sled', icon: '💎', needs: { diamond: 3 } },
    ],
    words: ['frost', 'snowy', 'glove', 'scarf', 'chill', 'skate', 'igloo', 'polar', 'winter', 'mitten', 'sledge', 'icicle', 'frozen', 'cocoa', 'penguin', 'blizzard', 'snowman', 'glacier', 'reindeer', 'freezing', 'snowball', 'mountain', 'summit', 'boots', 'north', 'walrus', 'husky', 'arctic', 'shiver', 'crystal', 'chimney', 'sweater'],
  },
  {
    name: 'Lava Caves', icon: '🌋', sky: ['#450a0a', '#9a3412'], night: ['#1c0505', '#450a0a'], grass: '#7f1d1d', dirt: ['#5c1414', '#6b1a1a'], deep: ['#1e1b2e', '#2e1065'],
    decor: 'none', speed: 7.5, sun: 'none', ceiling: '#1c0505', clouds: 0.25, enemy: { name: 'Fire Imp', skin: '#ef4444', hair: '#fbbf24', shirt: '#7f1d1d', pants: '#450a0a', eye: '#fde047' },
    materials: ['netherrack', 'magma', 'obsidian', 'gold'],
    recipes: [
      { name: 'Fire Shield', icon: '🛡️', needs: { netherrack: 3 } },
      { name: 'Magma Lamp', icon: '🏮', needs: { magma: 3 } },
      { name: 'Obsidian Portal', icon: '🌀', needs: { obsidian: 3, gold: 2 } },
    ],
    words: ['volcano', 'magma', 'ember', 'blazing', 'molten', 'dragon', 'crater', 'furnace', 'phoenix', 'sizzle', 'scorch', 'lantern', 'cinder', 'eruption', 'sulfur', 'obsidian', 'inferno', 'torches', 'glowing', 'burning', 'smolder', 'heatwave', 'fireball', 'wildfire', 'pressure', 'dungeon', 'cavern', 'tunnel', 'creature', 'warrior', 'courage', 'danger'],
  },
  {
    name: 'Sky Islands', icon: '☁️', sky: ['#a78bfa', '#fbcfe8'], night: ['#2e1065', '#581c87'], grass: '#86efac', dirt: ['#f1f5f9', '#ffffff'], deep: ['#c4b5fd', '#ddd6fe'],
    decor: 'none', speed: 8.5, enemy: { name: 'Phantom', skin: '#c4b5fd', shirt: '#6d28d9', pants: '#4c1d95', eye: '#f0abfc' },
    materials: ['cloud', 'crystal', 'star', 'diamond'],
    recipes: [
      { name: 'Cloud Bed', icon: '🛏️', needs: { cloud: 3 } },
      { name: 'Crystal Wand', icon: '🪄', needs: { crystal: 3 } },
      { name: 'Star Crown', icon: '👑', needs: { star: 3, diamond: 2 } },
    ],
    words: ['rainbow', 'thunder', 'lightning', 'floating', 'adventure', 'starlight', 'moonbeam', 'sparkle', 'twinkle', 'galaxy', 'heavenly', 'skyward', 'balloon', 'feathers', 'whisper', 'dreamland', 'kingdom', 'mystical', 'wonderful', 'champion', 'fantastic', 'magical', 'enormous', 'beautiful', 'celebrate', 'imagination', 'wizardry', 'gateway', 'legendary', 'victory', 'treasure', 'explorer'],
  },
  // Worlds 6-10. New worlds go at the end: a student's saved level is an index into this list.
  {
    name: 'Jungle Temple', icon: '🐒', sky: ['#22c55e', '#bbf7d0'], night: ['#052e16', '#14532d'], grass: '#16a34a', dirt: ['#5c3d1e', '#6b4724'], deep: ['#4d5b3a', '#5c6b47'],
    decor: 'palm', speed: 8, enemy: { name: 'Swamp Goblin', skin: '#65a30d', hair: '#3f6212', shirt: '#78350f', pants: '#422006', eye: '#fde047' },
    materials: ['leaves', 'bamboo', 'moss', 'emerald'],
    recipes: [
      { name: 'Vine Swing', icon: '🐒', needs: { leaves: 3 } },
      { name: 'Bamboo Raft', icon: '🛶', needs: { bamboo: 3, leaves: 2 } },
      { name: 'Jungle Camp', icon: '🏕️', needs: { moss: 3, bamboo: 2 } },
      { name: 'Emerald Idol', icon: '🗿', needs: { emerald: 3 } },
    ],
    words: ['vines', 'tiger', 'sloth', 'lemur', 'mango', 'monkey', 'parrot', 'jaguar', 'banana', 'bamboo', 'canopy', 'toucan', 'python', 'orchid', 'gorilla', 'panther', 'explore', 'tropical', 'monsoon', 'leopard', 'waterfall', 'chameleon', 'butterfly', 'crocodile', 'flamingo', 'rainforest', 'expedition', 'hummingbird', 'anaconda', 'tarantula', 'caterpillar', 'camouflage'],
  },
  {
    name: 'Ocean Reef', icon: '🐠', sky: ['#0891b2', '#67e8f9'], night: ['#082f49', '#0c4a6e'], grass: '#e8d39a', dirt: ['#dcc387', '#cfb676'], deep: ['#155e75', '#0e7490'],
    decor: 'kelp', speed: 8.5, sun: 'none', floaters: '🐟', enemy: { name: 'Pirate Ghost', skin: '#a7f3d0', hair: '#dc2626', shirt: '#1e3a8a', pants: '#111827', eye: '#facc15' },
    materials: ['sand', 'coral', 'seaglass', 'pearl'],
    recipes: [
      { name: 'Fish Tank', icon: '🐠', needs: { coral: 2, sand: 2 } },
      { name: 'Diving Helmet', icon: '🤿', needs: { seaglass: 3 } },
      { name: 'Treasure Chest', icon: '💰', needs: { pearl: 3, seaglass: 1 } },
    ],
    words: ['coral', 'pearl', 'shark', 'whale', 'squid', 'otter', 'scuba', 'oyster', 'anchor', 'lagoon', 'voyage', 'lobster', 'dolphin', 'octopus', 'mermaid', 'current', 'snorkel', 'seahorse', 'starfish', 'stingray', 'barnacle', 'seashell', 'navigate', 'tidepool', 'treasure', 'jellyfish', 'submarine', 'shipwreck', 'lighthouse', 'hammerhead'],
  },
  {
    name: 'Candy Kingdom', icon: '🍭', sky: ['#f9a8d4', '#fde68a'], night: ['#4a044e', '#86198f'], grass: '#f472b6', dirt: ['#a16207', '#854d0e'], deep: ['#7c2d12', '#9a3412'],
    decor: 'lollipop', speed: 9, enemy: { name: 'Gummy Goblin', skin: '#f472b6', hair: '#a855f7', shirt: '#a855f7', pants: '#7c3aed', eye: '#111' },
    materials: ['sugar', 'gumdrop', 'chocolate', 'peppermint'],
    recipes: [
      { name: 'Lollipop', icon: '🍭', needs: { sugar: 2, gumdrop: 2 } },
      { name: 'Chocolate Bar', icon: '🍫', needs: { chocolate: 3 } },
      { name: 'Candy Cane', icon: '🍬', needs: { peppermint: 3 } },
      { name: 'Gingerbread House', icon: '🏠', needs: { chocolate: 2, gumdrop: 2, peppermint: 2 } },
    ],
    words: ['taffy', 'fudge', 'sugar', 'donut', 'cookie', 'muffin', 'sundae', 'toffee', 'waffle', 'cupcake', 'caramel', 'gumdrop', 'pudding', 'pancake', 'dessert', 'licorice', 'lollipop', 'frosting', 'cinnamon', 'meringue', 'popsicle', 'sprinkles', 'jellybean', 'chocolate', 'peppermint', 'candyfloss', 'strawberry', 'gingerbread', 'marshmallow', 'butterscotch'],
  },
  {
    name: 'Haunted Woods', icon: '🎃', sky: ['#7c3aed', '#fb923c'], night: ['#020617', '#1e1b4b'], grass: '#365314', dirt: ['#3f2a14', '#4a3218'], deep: ['#3f3f46', '#52525b'],
    decor: 'deadtree', speed: 9.5, floaters: '🦇', enemy: { name: 'Scarecrow', skin: '#fde68a', hair: '#ca8a04', shirt: '#b45309', pants: '#1e3a8a', eye: '#111' },
    materials: ['darkwood', 'pumpkin', 'bone', 'soulgem'],
    recipes: [
      { name: 'Jack-o\'-Lantern', icon: '🎃', needs: { pumpkin: 3 } },
      { name: 'Bone Ladder', icon: '🦴', needs: { bone: 3, darkwood: 2 } },
      { name: 'Magic Potion', icon: '🧪', needs: { soulgem: 3 } },
    ],
    words: ['ghost', 'witch', 'broom', 'spooky', 'shadow', 'candle', 'goblin', 'wizard', 'potion', 'spider', 'cobweb', 'creepy', 'pumpkin', 'lantern', 'mystery', 'haunted', 'whisper', 'costume', 'vampire', 'mansion', 'phantom', 'midnight', 'skeleton', 'cauldron', 'werewolf', 'scarecrow', 'moonlight', 'enchanted', 'spellbook', 'shivering'],
  },
  {
    name: 'Moon Base', icon: '🌕', sky: ['#0f172a', '#1e293b'], night: ['#000000', '#0f172a'], grass: '#d4d4d8', dirt: ['#a1a1aa', '#b4b4bb'], deep: ['#52525b', '#71717a'],
    decor: 'flag', speed: 10, sun: 'earth', clouds: 0, stars: true, enemy: { name: 'Moon Alien', skin: '#86efac', shirt: '#475569', pants: '#1e293b', eye: '#111' },
    materials: ['moonrock', 'metal', 'goo', 'meteor'],
    recipes: [
      { name: 'Moon Boots', icon: '👢', needs: { moonrock: 2, metal: 1 } },
      { name: 'Moon Rover', icon: '🚙', needs: { metal: 3, moonrock: 2 } },
      { name: 'Satellite', icon: '🛰️', needs: { goo: 3, metal: 2 } },
      { name: 'Rocket Ship', icon: '🚀', needs: { meteor: 3 } },
    ],
    words: ['lunar', 'orbit', 'comet', 'alien', 'rover', 'rocket', 'crater', 'planet', 'meteor', 'oxygen', 'helmet', 'gravity', 'capsule', 'station', 'mission', 'eclipse', 'asteroid', 'galactic', 'starship', 'moonwalk', 'launchpad', 'astronaut', 'satellite', 'spaceship', 'telescope', 'universe', 'countdown', 'spacewalk', 'supernova', 'constellation'],
  },
];

const SLOT_X = [30, 48, 66, 84]; // block centers, % of width
const HEIGHT = 440;
const SURFACE_Y = 200;
const PLAYER_X = 7;
const DAY_LENGTH = 60; // seconds per day; night is the last third
const PLAYER: Character = { name: 'You', skin: '#f5c9a0', hair: '#5b3a1a', shirt: '#f97316', pants: '#1d4ed8', eye: '#1e3a8a' };

interface Block { id: number; type: Mat; word: string; typed: number }
interface Enemy { id: number; word: string; typed: number; x: number; speed: number }
interface Bit { id: number; x: number; y: number; color: string; dx: number; dy: number; at: number }

// Deterministic 4x4 pixel texture so a block doesn't flicker between renders.
const texture = (type: Mat, seed: number) => {
  const m = MATS[type];
  let s = seed * 7919 + 13;
  const rnd = () => { s = (s * 9301 + 49297) % 233280; return s / 233280; };
  const px: string[] = [];
  for (let i = 0; i < 16; i++) {
    if (m.stripes) px.push(m.base[(i % 4 % 2) * 2 + (rnd() < 0.3 ? 1 : 0)]);
    else px.push(m.base[Math.floor(rnd() * m.base.length)]);
  }
  if (m.speck) for (let k = 0; k < 4; k++) px[Math.floor(rnd() * 16)] = m.speck;
  return px;
};

const PixelBlock: React.FC<{ type: Mat; seed: number; size: number }> = ({ type, seed, size }) => {
  const px = useMemo(() => texture(type, seed), [type, seed]);
  return (
    <div className="grid grid-cols-4 grid-rows-4 box-border" style={{ width: size, height: size, border: `${size < 24 ? 1 : 3}px solid rgba(0,0,0,0.3)` }}>
      {px.map((c, i) => <span key={i} style={{ background: c }} />)}
    </div>
  );
};

// A blocky character built from rectangles.
const Blocky: React.FC<{ c: Character; flip?: boolean; walking?: boolean }> = ({ c, flip, walking }) => (
  <div className="relative" style={{ width: 32, height: 80, transform: flip ? 'scaleX(-1)' : undefined }}>
    <div className="absolute" style={{ left: 2, top: 0, width: 28, height: 28, background: c.skin, border: '2px solid rgba(0,0,0,0.25)' }}>
      {c.hair && <div className="absolute inset-x-0 top-0" style={{ height: 8, background: c.hair }} />}
      <span className="absolute" style={{ left: 14, top: 12, width: 5, height: 5, background: c.eye }} />
      <span className="absolute" style={{ left: 21, top: 12, width: 5, height: 5, background: c.eye }} />
    </div>
    <div className="absolute" style={{ left: 0, top: 28, width: 32, height: 28, background: c.shirt, border: '2px solid rgba(0,0,0,0.2)' }} />
    <div className={`absolute ${walking ? 'bm-leg-a' : ''}`} style={{ left: 1, top: 56, width: 14, height: 24, background: c.pants, border: '2px solid rgba(0,0,0,0.2)' }} />
    <div className={`absolute ${walking ? 'bm-leg-b' : ''}`} style={{ left: 17, top: 56, width: 14, height: 24, background: c.pants, border: '2px solid rgba(0,0,0,0.2)' }} />
  </div>
);

const Decor: React.FC<{ kind: World['decor'] }> = ({ kind }) => {
  if (kind === 'none') return null;
  const box = (l: number, t: number, w: number, h: number, bg: string) =>
    <div className="absolute" style={{ left: l, top: t, width: w, height: h, background: bg, border: '3px solid rgba(0,0,0,0.2)' }} />;
  return (
    <div className="absolute" style={{ left: '91%', top: SURFACE_Y - 110 }} aria-hidden="true">
      {kind === 'tree' && <>{box(-26, 0, 70, 56, '#2f9e44')}{box(0, 56, 18, 54, '#8a5a2b')}</>}
      {kind === 'cactus' && <>{box(0, 20, 22, 90, '#2f9e44')}{box(-18, 44, 18, 14, '#2f9e44')}{box(-18, 28, 12, 20, '#2f9e44')}{box(22, 56, 16, 12, '#2f9e44')}{box(30, 40, 12, 20, '#2f9e44')}</>}
      {kind === 'pine' && <>{box(-22, 0, 62, 26, '#f8fafc')}{box(-30, 26, 78, 34, '#166534')}{box(0, 60, 18, 50, '#6b4423')}</>}
      {kind === 'palm' && <>{box(0, 24, 16, 86, '#a16207')}{box(-30, 8, 76, 22, '#16a34a')}{box(-14, 0, 44, 14, '#22c55e')}</>}
      {kind === 'kelp' && <>{box(-16, 30, 10, 80, '#166534')}{box(0, 6, 10, 104, '#15803d')}{box(16, 44, 10, 66, '#16a34a')}</>}
      {kind === 'lollipop' && <>{box(6, 44, 8, 66, '#f8fafc')}{box(-16, 0, 52, 46, '#f472b6')}{box(-4, 12, 28, 22, '#fde047')}</>}
      {kind === 'deadtree' && <>{box(0, 20, 16, 90, '#3f2a14')}{box(-26, 34, 26, 8, '#3f2a14')}{box(16, 12, 28, 8, '#3f2a14')}{box(-14, 4, 8, 32, '#3f2a14')}</>}
      {kind === 'flag' && <>{box(0, 10, 6, 100, '#e5e7eb')}{box(6, 10, 46, 28, '#ef4444')}</>}
    </div>
  );
};

const newState = (world: number) => ({
  world, blocks: [] as Block[], enemies: [] as Enemy[], bits: [] as Bit[],
  locked: null as string | null, inv: Object.fromEntries(Object.keys(MATS).map(k => [k, 0])) as Record<Mat, number>,
  recipe: 0, crafted: [] as string[], hearts: 3, score: 0, correct: 0, mistakes: 0, mined: 0, beaten: 0,
  time: 0, spawnTimer: 0, nextId: 1, swings: 0, banner: null as { text: string; at: number } | null, hurtAt: 0, won: false,
});
type S = ReturnType<typeof newState>;

const BlockMinerGame: React.FC<{ user?: User }> = ({ user }) => {
  const [gameState, setGameState] = useState<GameState>('ready');
  const [, setFrame] = useState(0);
  const [unlocked, unlock, levelsLoaded] = useLevelProgress('block-miner', user);
  const [world, setWorld] = useState(() => Math.min(unlocked, WORLDS.length - 1));
  useEffect(() => { if (levelsLoaded) setWorld(Math.min(unlocked, WORLDS.length - 1)); }, [levelsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps
  const areaRef = useRef<HTMLDivElement>(null);
  const { pops, pop } = useScorePops();
  const g = useRef(newState(0));

  const W = WORLDS[g.current.world];

  // Tier i of a world's materials gets longer words than tier i-1.
  const pickWord = (s: S, tier: number) => {
    const words = WORLDS[s.world].words;
    const lens = words.map(w => w.length);
    const min = Math.min(...lens) + tier;
    const usedFirst = new Set([...s.blocks.map(b => b.word[0]), ...s.enemies.map(e => e.word[0])]);
    const pool = words.filter(w => w.length >= min && w.length <= min + 2);
    const source = pool.length >= 4 ? pool : words;
    for (let i = 0; i < 30; i++) {
      const w = source[Math.floor(Math.random() * source.length)];
      if (!usedFirst.has(w[0])) return w;
    }
    return source[Math.floor(Math.random() * source.length)];
  };

  // Favor what the current recipe still needs so kids aren't stuck mining filler.
  const pickType = (s: S): Mat => {
    const mats = WORLDS[s.world].materials;
    const unlockedMats = mats.slice(0, Math.min(mats.length, 2 + s.recipe));
    const recipe = WORLDS[s.world].recipes[s.recipe];
    if (recipe && Math.random() < 0.65) {
      const onBoard = (m: Mat) => s.blocks.filter(b => b.type === m).length;
      const needed = (Object.keys(recipe.needs) as Mat[]).filter(m => unlockedMats.includes(m) && s.inv[m] + onBoard(m) < (recipe.needs[m] ?? 0));
      if (needed.length) return needed[Math.floor(Math.random() * needed.length)];
    }
    return unlockedMats[Math.floor(Math.random() * unlockedMats.length)];
  };

  const makeBlock = (s: S, type = pickType(s)): Block =>
    ({ id: s.nextId++, type, word: pickWord(s, WORLDS[s.world].materials.indexOf(type)), typed: 0 });

  const startGame = (w = world) => {
    const s = newState(w);
    const firstNeed = Object.keys(WORLDS[w].recipes[0].needs)[0] as Mat;
    for (let i = 0; i < SLOT_X.length; i++) s.blocks.push(makeBlock(s, i < 2 ? firstNeed : undefined));
    g.current = s;
    setWorld(w);
    setGameState('playing');
  };

  useSaveGameResult(user, 'block-miner', gameState, g.current.score);

  const burst = (s: S, x: number, y: number, colors: string[], now: number) => {
    for (let i = 0; i < 10; i++) {
      const a = (Math.PI * 2 * i) / 10 + Math.random() * 0.5;
      s.bits.push({ id: s.nextId++, x, y, color: colors[i % colors.length], dx: Math.cos(a) * (30 + Math.random() * 30), dy: Math.sin(a) * (30 + Math.random() * 30) - 20, at: now });
    }
  };

  // Game loop: day/night clock, monsters, particles.
  useEffect(() => {
    if (gameState !== 'playing') return;
    let raf = 0;
    let last = performance.now();
    const loop = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const s = g.current;
      const w = WORLDS[s.world];
      s.time += dt;
      const t = s.time % DAY_LENGTH;
      const isNight = t >= 40 && t < 57;

      if (isNight) {
        s.spawnTimer -= dt;
        if (s.spawnTimer <= 0 && s.enemies.length < Math.min(4, 1 + s.recipe)) {
          s.enemies.push({ id: s.nextId++, word: pickWord(s, 1), typed: 0, x: 102, speed: w.speed + s.recipe * 0.8 + Math.random() * 1.5 });
          s.spawnTimer = Math.max(2.2, 4.5 - s.recipe * 0.5);
        }
      } else if (s.enemies.length) {
        // Sunrise: monsters vanish, no penalty.
        for (const e of s.enemies) {
          if (s.locked === `e${e.id}`) s.locked = null;
          burst(s, e.x, SURFACE_Y - 50, ['#f97316', '#facc15', '#ef4444'], now);
        }
        s.enemies = [];
        s.spawnTimer = 0;
      }

      for (const e of s.enemies) e.x -= e.speed * dt;
      const reached = s.enemies.filter(e => e.x <= PLAYER_X + 6);
      if (reached.length) {
        s.enemies = s.enemies.filter(e => e.x > PLAYER_X + 6);
        if (reached.some(e => s.locked === `e${e.id}`)) s.locked = null;
        s.hearts -= reached.length;
        s.hurtAt = now;
        if (s.hearts <= 0) {
          s.hearts = 0;
          playGameOverSound();
          setGameState('over');
          return;
        }
        playMissSound();
      }
      s.bits = s.bits.filter(b => now - b.at < 700);
      setFrame(f => f + 1);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState]);

  // Typing: first letter locks a target (monsters first), then finish the word.
  useEffect(() => {
    if (gameState !== 'playing') return;
    const onKey = (e: KeyboardEvent) => {
      const s = g.current;
      const w = WORLDS[s.world];
      if (e.key === 'Backspace') { e.preventDefault(); s.locked = null; return; }
      if (!isTypingKey(e)) return;
      e.preventDefault();
      const letter = e.key.toLowerCase();
      const now = performance.now();

      let enemy = s.enemies.find(x => `e${x.id}` === s.locked);
      let block = s.blocks.find(b => `b${b.id}` === s.locked);
      if (!enemy && !block) {
        enemy = s.enemies.filter(x => x.word[0] === letter).sort((a, b) => a.x - b.x)[0];
        if (enemy) s.locked = `e${enemy.id}`;
        else {
          block = s.blocks.find(b => b.word[0] === letter);
          if (block) s.locked = `b${block.id}`;
        }
      }
      const target = enemy ?? block;
      if (!target || target.word[target.typed] !== letter) {
        s.mistakes++;
        playWrongSound();
        return;
      }
      target.typed++;
      s.correct++;
      s.swings++;
      if (target.typed < target.word.length) { playCorrectSound(); return; }

      s.locked = null;
      if (enemy) {
        s.enemies = s.enemies.filter(x => x.id !== enemy!.id);
        s.beaten++;
        const gained = 30 + enemy.word.length * 5;
        s.score += gained;
        burst(s, enemy.x, SURFACE_Y - 50, [w.enemy.skin, w.enemy.shirt, '#e5e7eb'], now);
        pop(`+${gained}`, enemy.x, 20, '#16a34a');
        playPopSound();
        return;
      }

      // Block mined
      const b = block!;
      const slot = s.blocks.findIndex(x => x.id === b.id);
      const m = MATS[b.type];
      s.inv[b.type]++;
      s.mined++;
      s.score += m.pts;
      burst(s, SLOT_X[slot], 290, m.speck ? [m.speck, ...m.base] : m.base, now);
      pop(`+1 ${m.label}`, SLOT_X[slot], 48, '#facc15');
      playPopSound();
      s.blocks[slot] = makeBlock(s);

      const recipe = w.recipes[s.recipe];
      if (recipe && (Object.entries(recipe.needs) as [Mat, number][]).every(([k, n]) => s.inv[k] >= n)) {
        for (const [k, n] of Object.entries(recipe.needs) as [Mat, number][]) s.inv[k] -= n;
        s.crafted.push(recipe.icon);
        s.score += 100 * (s.recipe + 1) * (s.world + 1);
        s.recipe++;
        playSuccessSound();
        if (s.recipe >= w.recipes.length) {
          s.won = true;
          s.score += s.hearts * 100;
          unlock(Math.min(WORLDS.length - 1, s.world + 1));
          setGameState('over');
          return;
        }
        const newMat = w.materials[1 + s.recipe];
        s.banner = { text: `Crafted ${recipe.name} ${recipe.icon}${newMat ? `  ${MATS[newMat].label} unlocked!` : ''}`, at: now };
        if (newMat) {
          // Show the new material right away.
          const at = s.blocks.findIndex(x => x.typed === 0);
          if (at >= 0) s.blocks[at] = makeBlock(s, newMat);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState, pop]);

  const s = g.current;
  const accuracy = s.correct + s.mistakes > 0 ? Math.round((s.correct / (s.correct + s.mistakes)) * 100) : 100;
  const button = 'bg-gradient-to-r from-emerald-500 to-lime-500';

  if (gameState === 'ready') {
    return (
      <GameIntro icon="⛏️" title="Block Miner" skill="accurate word typing -- every letter is a swing of your pickaxe." accent="text-emerald-600" button={button} onStart={() => startGame()}>
        <p>Type the word on a block to mine it. Collect materials and craft every item on the list to finish the world. When night falls, monsters walk in -- type their words to stop them! Each world has new words, blocks and monsters. Press Backspace to switch targets.</p>
        <p className="mt-4 font-bold text-gray-700">Choose a world:</p>
        <LevelPicker levels={WORLDS} unlocked={unlocked} selected={world} onSelect={setWorld} />
      </GameIntro>
    );
  }
  if (gameState === 'over') {
    const next = s.world + 1 < WORLDS.length ? WORLDS[s.world + 1] : null;
    return (
      <GameOver
        title={s.won ? `${W.name} Complete!` : 'Game Over!'}
        message={s.won ? (next ? `You unlocked World ${s.world + 2}: ${next.name} ${next.icon}` : 'You beat every world -- legendary miner! 🏆') : `The ${W.enemy.name.toLowerCase()}s got you this time. Dig back in!`}
        stars={s.won ? s.hearts : s.mined > 0 ? 1 : 0}
        stats={[{ label: 'Score', value: s.score }, { label: 'Blocks Mined', value: s.mined }, { label: `${W.enemy.name}s`, value: s.beaten }, { label: 'Accuracy', value: `${accuracy}%` }]}
        button={button}
        onRestart={() => startGame(s.world)}
      >
        {s.crafted.length > 0 && <div className="mt-3 text-4xl tracking-widest" aria-label="Crafted items">{s.crafted.join(' ')}</div>}
        {s.won && next && (
          <button onClick={() => startGame(s.world + 1)} className="fun-shine mt-6 px-8 py-3 text-white rounded-full font-black text-xl shadow-lg ring-4 ring-white bg-gradient-to-r from-violet-500 to-fuchsia-500 transition-transform hover:scale-105 active:scale-95">
            Next World: {next.name} {next.icon} ▶
          </button>
        )}
      </GameOver>
    );
  }

  const width = areaRef.current?.offsetWidth ?? 800;
  const blockSize = width < 520 ? 48 : 64;
  const t = s.time % DAY_LENGTH;
  const night = t < 36 ? 0 : t < 40 ? (t - 36) / 4 : t < 56 ? 1 : t < 60 ? (60 - t) / 4 : 0;
  const recipe = W.recipes[s.recipe];
  const now = performance.now();
  const hurt = now - s.hurtAt < 300;
  const unlockedCount = Math.min(W.materials.length, 2 + s.recipe);

  return (
    <div className="w-full">
      <div className="flex flex-wrap justify-around items-center mb-4 gap-3 p-3 bg-white/60 rounded-2xl shadow-inner">
        <StatBox label="Score" value={s.score} />
        <StatBox label="World" value={`${W.icon} ${s.world + 1}`} />
        <StatBox label="Mined" value={s.mined} />
        <div className="flex flex-col items-center px-4 py-2 min-w-[80px] rounded-2xl bg-white shadow-md ring-2 ring-white/70">
          <span className="text-[11px] font-extrabold uppercase tracking-wider text-rose-500">Hearts</span>
          <span className="text-2xl tracking-wider" aria-label={`${s.hearts} ${s.hearts === 1 ? 'heart' : 'hearts'} left`}>
            {'❤️'.repeat(s.hearts)}<span className="opacity-20">{'🖤'.repeat(3 - s.hearts)}</span>
          </span>
        </div>
      </div>

      <div ref={areaRef} className="relative overflow-hidden rounded-2xl border-4 border-white shadow-inner select-none" style={{ height: HEIGHT }}>
        {/* Sky: day layer with a night layer faded over it */}
        <div className="absolute inset-0" style={{ background: `linear-gradient(${W.sky[0]}, ${W.sky[1]})` }} />
        <div className="absolute inset-0" style={{ background: `linear-gradient(${W.night[0]}, ${W.night[1]})`, opacity: night }} />
        {W.stars && Array.from({ length: 36 }, (_, i) => (
          <span key={`st${i}`} className="absolute bg-white" style={{ left: `${(i * 37) % 100}%`, top: (i * 53) % 180, width: i % 4 ? 2 : 3, height: i % 4 ? 2 : 3, opacity: 0.8 }} aria-hidden="true" />
        ))}
        {!W.sun && <>
          <div className="absolute right-[8%] top-6 w-12 h-12 bg-yellow-300 border-4 border-yellow-200" style={{ opacity: 1 - night }} aria-hidden="true" />
          <div className="absolute right-[8%] top-6 w-10 h-10 bg-slate-100 border-4 border-slate-300" style={{ opacity: night }} aria-hidden="true" />
        </>}
        {W.sun === 'earth' && <span className="absolute right-[8%] top-4 text-5xl" aria-hidden="true">🌍</span>}
        {W.ceiling && <div className="absolute inset-x-0 top-0 h-6" style={{ background: `repeating-linear-gradient(90deg, ${W.ceiling} 0 20px, transparent 20px 40px)` }} aria-hidden="true" />}
        {[{ top: 24, w: 90, d: 0 }, { top: 60, w: 64, d: -14 }, { top: 36, w: 110, d: -28 }].map((c, i) => W.floaters
          ? <span key={i} className="absolute bm-cloud text-3xl" style={{ top: c.top + i * 20, animationDelay: `${c.d}s` }} aria-hidden="true">{W.floaters}</span>
          : <div key={i} className="absolute bm-cloud bg-white/90" style={{ top: c.top, width: c.w, height: 22, animationDelay: `${c.d}s`, opacity: (1 - night * 0.6) * (W.clouds ?? 1) }} aria-hidden="true" />
        )}
        <Decor kind={W.decor} />

        {/* Ground: surface strip, then underground layers */}
        <div className="absolute inset-x-0" style={{ top: SURFACE_Y, height: 14, background: W.grass, borderBottom: '4px solid rgba(0,0,0,0.2)' }} />
        <div className="absolute inset-x-0 bottom-0" style={{ top: SURFACE_Y + 14, background: `repeating-linear-gradient(90deg, ${W.dirt[0]} 0 24px, ${W.dirt[1]} 24px 48px)` }} />
        <div className="absolute inset-x-0 bottom-0 h-24" style={{ background: `repeating-linear-gradient(90deg, ${W.deep[0]} 0 24px, ${W.deep[1]} 24px 48px)` }} />
        {/* Dark mine tunnel the blocks sit in */}
        <div className="absolute rounded-md" style={{ left: '20%', right: '7%', top: 228, height: 120, background: 'linear-gradient(#1c130b, #2b1d10)', boxShadow: 'inset 0 6px 12px rgba(0,0,0,0.6)' }}>
          <span className="absolute left-2 top-2 text-lg" aria-hidden="true">🔥</span>
          <span className="absolute right-2 top-2 text-lg" aria-hidden="true">🔥</span>
        </div>

        {/* Quest */}
        {recipe && (
          <div className="absolute left-3 top-3 z-20 px-3 py-2 rounded-lg bg-black/50 text-white text-xs md:text-sm font-bold max-w-[60%]">
            <div className="mb-1">📜 Craft: {recipe.name} {recipe.icon} <span className="opacity-70">({s.recipe + 1}/{W.recipes.length})</span></div>
            <div className="flex flex-wrap gap-3">
              {(Object.entries(recipe.needs) as [Mat, number][]).map(([m, n]) => (
                <span key={m} className={`inline-flex items-center gap-1 ${s.inv[m] >= n ? 'text-lime-300' : ''}`}>
                  <PixelBlock type={m} seed={3} size={16} /> {Math.min(s.inv[m], n)}/{n}
                </span>
              ))}
            </div>
          </div>
        )}

        {s.banner && now - s.banner.at < 2600 && (
          <div key={s.banner.at} className="fun-pop absolute left-1/2 -translate-x-1/2 top-24 z-30 px-4 py-2 rounded-xl bg-yellow-300 text-amber-900 font-black text-sm md:text-lg shadow-lg ring-4 ring-white whitespace-nowrap">
            ✨ {s.banner.text}
          </div>
        )}
        {s.time < 3 && !s.banner && (
          <div className="fun-pop absolute left-1/2 -translate-x-1/2 top-24 z-30 px-4 py-2 rounded-xl bg-white text-violet-700 font-black text-lg shadow-lg ring-4 ring-violet-200 whitespace-nowrap">
            World {s.world + 1}: {W.name} {W.icon}
          </div>
        )}

        {/* Player */}
        <div className="absolute z-10" style={{ left: `${PLAYER_X}%`, top: SURFACE_Y - 80 }}>
          <Blocky c={PLAYER} />
          <span key={s.swings} className="absolute bm-swing text-3xl" style={{ left: 22, top: 18 }} aria-hidden="true">⛏️</span>
        </div>

        {/* Monsters */}
        {s.enemies.map(e => {
          const isLocked = s.locked === `e${e.id}`;
          return (
            <div key={e.id} className="absolute z-10 -translate-x-1/2 flex flex-col items-center" style={{ left: `${e.x}%`, top: SURFACE_Y - 112 }}>
              <span className={`mb-1 px-2 py-0.5 rounded-md font-mono text-base md:text-lg font-bold border-2 shadow-md whitespace-nowrap ${isLocked ? 'bg-rose-500 border-white scale-110' : 'bg-black/70 border-white/60'}`}>
                <span className="text-yellow-200">{e.word.slice(0, e.typed)}</span><span className="text-white">{e.word.slice(e.typed)}</span>
              </span>
              <Blocky c={W.enemy} flip walking />
            </div>
          );
        })}

        {/* Mineable blocks */}
        {s.blocks.map((b, i) => {
          const isLocked = s.locked === `b${b.id}`;
          const crack = b.typed / b.word.length;
          return (
            <div key={b.id} className="absolute -translate-x-1/2 flex flex-col items-center" style={{ left: `${SLOT_X[i]}%`, top: 236 }}>
              <span className={`mb-2 px-2 py-0.5 rounded-md font-mono text-base md:text-lg font-bold border-2 shadow-md whitespace-nowrap ${isLocked ? 'bg-amber-400 border-white scale-110' : 'bg-black/60 border-white/60'}`}>
                <span className={isLocked ? 'text-white' : 'text-yellow-200'}>{b.word.slice(0, b.typed)}</span><span className={isLocked ? 'text-amber-900' : 'text-white'}>{b.word.slice(b.typed)}</span>
              </span>
              <div className={`relative fun-pop ${isLocked ? 'ring-4 ring-white' : ''}`}>
                <PixelBlock type={b.type} seed={b.id} size={blockSize} />
                {crack > 0 && (
                  <svg className="absolute inset-0 w-full h-full" viewBox="0 0 16 16" aria-hidden="true">
                    <path d="M8 0 L7 4 L9 6 L6 9" stroke="#111" strokeWidth="0.9" fill="none" opacity={0.8} />
                    <path d="M6 9 L3 12 M9 6 L13 5" stroke="#111" strokeWidth="0.9" fill="none" opacity={crack > 0.3 ? 0.8 : 0} />
                    <path d="M6 9 L8 13 L7 16 M13 5 L16 7 M3 12 L0 11" stroke="#111" strokeWidth="0.9" fill="none" opacity={crack > 0.6 ? 0.8 : 0} />
                  </svg>
                )}
              </div>
            </div>
          );
        })}

        {s.bits.map(b => (
          <span key={b.id} className="absolute bm-bit pointer-events-none" style={{ left: `${b.x}%`, top: b.y, width: 9, height: 9, background: b.color, ['--dx' as string]: `${b.dx}px`, ['--dy' as string]: `${b.dy}px` } as React.CSSProperties} />
        ))}

        {night > 0.5 && s.enemies.length === 0 && t < 44 && (
          <div className="absolute left-1/2 -translate-x-1/2 top-28 z-20 px-3 py-1 rounded-lg bg-black/60 text-white font-bold text-sm">🌙 Night falls... {W.enemy.name.toLowerCase()}s are coming!</div>
        )}
        {hurt && <div className="absolute inset-0 z-30 bg-red-500/30 pointer-events-none" />}
        <ScorePops pops={pops} />

        <style>{`
          .bm-cloud { animation: bm-cloud 40s linear infinite; left: 100%; }
          @keyframes bm-cloud { from { transform: translateX(0); } to { transform: translateX(calc(-100vw - 200px)); } }
          .bm-swing { animation: bm-swing 0.25s ease-out; transform-origin: 20% 80%; }
          @keyframes bm-swing { 0% { transform: rotate(-60deg); } 100% { transform: rotate(10deg); } }
          .bm-leg-a { animation: bm-step 0.5s ease-in-out infinite; transform-origin: top; }
          .bm-leg-b { animation: bm-step 0.5s ease-in-out infinite reverse; transform-origin: top; }
          @keyframes bm-step { 0%, 100% { transform: rotate(18deg); } 50% { transform: rotate(-18deg); } }
          .bm-bit { animation: bm-bit 0.7s ease-out forwards; }
          @keyframes bm-bit { to { transform: translate(var(--dx), calc(var(--dy) + 60px)); opacity: 0; } }
        `}</style>
      </div>

      {/* Hotbar */}
      <div className="mt-3 flex flex-wrap items-center justify-center gap-1.5 p-2 rounded-xl bg-[#3f3f46] border-4 border-[#27272a] shadow-lg">
        {W.materials.map((m, i) => {
          const open = i < unlockedCount;
          return (
            <div key={m} className="relative w-14 h-14 flex items-center justify-center bg-[#8b8b8b] border-4 border-t-[#555] border-l-[#555] border-b-[#c6c6c6] border-r-[#c6c6c6]" title={MATS[m].label}>
              {open ? <PixelBlock type={m} seed={i + 7} size={32} /> : <span className="text-xl opacity-60">🔒</span>}
              {open && <span className="absolute bottom-0 right-1 text-white font-black text-sm" style={{ textShadow: '2px 2px 0 #3f3f3f' }}>{s.inv[m]}</span>}
            </div>
          );
        })}
        {s.crafted.length > 0 && <div className="ml-2 text-3xl" aria-label="Crafted items">{s.crafted.join(' ')}</div>}
      </div>
      <p className="text-center text-sm text-gray-500 mt-3">Just start typing -- monsters get targeted first. Backspace switches targets.</p>
    </div>
  );
};

export default BlockMinerGame;
